import { useRef, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { IANA_TIMEZONE_LABELS, type IANATimezone } from '@truecare/schemas';
import { Alert, Button, Field, Input, StatusBadge, useFocusTrap } from '@truecare/ui';
import type {
  AdminFacilityListItem,
  AdminFacilityListResponse,
} from '@truecare/schemas';
import { messageOf, unwrap } from '@truecare/api-client';
import { api } from '../../api';
import { adminBeforeLoad } from '../../lib/admin-before-load';
import { AdminLayout } from '../../components/AdminLayout';
import {
  AdminTable,
  type AdminTableColumn,
} from '../../components/AdminTable';
import {
  AdminListFilters,
  type FilterField,
} from '../../components/AdminListFilters';

/**
 * Phase 14b-2 — Facilities list (persona-tailored columns per
 * 14b-2-5; operational filters per 14b-2-4 including contractState +
 * creditHoldStatus added in this commit's backend extension).
 */

const SearchSchema = z.object({
  status: z.string().optional(),
  type: z.string().optional(),
  contractState: z.string().optional(),
  creditHoldStatus: z.string().optional(),
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(25),
});

type Search = z.infer<typeof SearchSchema>;

export const Route = createFileRoute('/facilities/')({
  beforeLoad: () => adminBeforeLoad('/facilities'),
  validateSearch: (search) => SearchSchema.parse(search),
  component: FacilitiesListPage,
});

const STATUS_OPTIONS = [
  { value: 'PROSPECT', label: 'Prospect' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'SUSPENDED', label: 'Suspended' },
  { value: 'TERMINATED', label: 'Terminated' },
];

const TYPE_OPTIONS = [
  { value: 'LTC_HOME', label: 'LTC home' },
  { value: 'RETIREMENT_RESIDENCE', label: 'Retirement residence' },
  { value: 'HOSPITAL', label: 'Hospital' },
  { value: 'OTHER', label: 'Other' },
];

const CONTRACT_STATE_OPTIONS = [
  { value: 'active', label: 'Active contract' },
  { value: 'expired', label: 'Expired (no active)' },
  { value: 'none', label: 'No contract' },
];

const CREDIT_HOLD_OPTIONS = [
  { value: 'NONE', label: 'No hold' },
  { value: 'WARNED', label: 'Warned' },
  { value: 'ACTIVE', label: 'On hold' },
];

const FILTER_FIELDS: FilterField[] = [
  { name: 'status', label: 'Status', type: 'select', options: STATUS_OPTIONS },
  { name: 'type', label: 'Type', type: 'select', options: TYPE_OPTIONS },
  {
    name: 'contractState',
    label: 'Contract',
    type: 'select',
    options: CONTRACT_STATE_OPTIONS,
  },
  {
    name: 'creditHoldStatus',
    label: 'Credit hold',
    type: 'select',
    options: CREDIT_HOLD_OPTIONS,
  },
];

// Column order (Phase 17 M2): the decision badges (Status, Contract, Credit
// hold) follow Name directly so they stay visible on a phone-width table;
// reference columns (Code, Added) sit after them.
const COLUMNS: AdminTableColumn<AdminFacilityListItem>[] = [
  { key: 'name', header: 'Name' },
  {
    key: 'status',
    header: 'Status',
    width: 'w-28',
    render: (r) => <StatusBadge status={r.status} />,
  },
  {
    key: 'contractState',
    header: 'Contract',
    width: 'w-32',
    render: (r) => <StatusBadge status={r.contractState} />,
  },
  {
    key: 'creditHoldStatus',
    header: 'Credit hold',
    width: 'w-28',
    render: (r) =>
      r.creditHoldStatus === 'NONE' ? (
        <span className="text-ink-muted">—</span>
      ) : (
        // credit-hold ACTIVE is BAD (inverted from the usual meaning), so pass
        // an explicit tone rather than let the default map read it as success.
        <StatusBadge
          status={r.creditHoldStatus}
          tone={r.creditHoldStatus === 'WARNED' ? 'warning' : 'danger'}
          label={r.creditHoldStatus === 'WARNED' ? 'Warned' : 'On hold'}
        />
      ),
  },
  {
    key: 'code',
    header: 'Code',
    width: 'w-24',
    sortable: false,
    render: (r) =>
      r.code ? (
        <span className="font-mono text-xs">{r.code}</span>
      ) : (
        <span className="text-ink-muted">—</span>
      ),
  },
  {
    key: 'createdAt',
    header: 'Added',
    width: 'w-32',
    render: (r) => new Date(r.createdAt).toLocaleDateString('en-CA'),
  },
];

function FacilitiesListPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const query = useQuery<AdminFacilityListResponse>({
    queryKey: ['admin', 'facilities', search],
    queryFn: async () =>
      (await unwrap(
        api.GET('/api/v1/admin/facilities', {
          params: {
            query: {
              status: search.status,
              type: search.type,
              contractState: search.contractState,
              creditHoldStatus: search.creditHoldStatus,
              page: search.page,
              pageSize: search.limit,
            },
          },
        } as never),
      )) as unknown as AdminFacilityListResponse,
  });

  function updateSearch(next: Partial<Search>): void {
    navigate({ search: { ...search, ...next } as Search });
  }

  const [createOpen, setCreateOpen] = useState(false);

  return (
    <AdminLayout>
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-heading text-3xl text-forest md:text-4xl">Facilities</h1>
          <p className="mt-1 text-sm text-ink-muted">
            Long-term care homes, retirement residences, hospitals, and other
            facilities. Click a row to open its detail page.
          </p>
        </div>
        <Button
          variant="primary"
          onClick={() => setCreateOpen(true)}
          data-testid="facility-create-open"
        >
          + New facility
        </Button>
      </div>

      {createOpen && (
        <NewFacilityModal onClose={() => setCreateOpen(false)} />
      )}

      {/* AdminListFilters renders its own bordered bar — no outer Card wrapper
          (Phase 17 M5: avoid the box-in-box above the table). */}
      <div className="mt-6">
        <AdminListFilters
          fields={FILTER_FIELDS}
          values={{
            status: search.status,
            type: search.type,
            contractState: search.contractState,
            creditHoldStatus: search.creditHoldStatus,
          }}
          onChange={(next) =>
            updateSearch({ ...next, page: 1 } as Partial<Search>)
          }
        />
      </div>

      <div className="mt-6" data-testid="facilities-list-table">
        <AdminTable<AdminFacilityListItem>
          columns={COLUMNS}
          rows={query.data?.items ?? []}
          isLoading={query.isPending}
          isError={query.isError}
          emptyMessage="No facilities match these filters."
          page={search.page}
          limit={search.limit}
          totalCount={query.data?.totalCount ?? 0}
          onPageChange={(page) => updateSearch({ page })}
          onLimitChange={(limit) => updateSearch({ limit, page: 1 })}
          onRowClick={(row) =>
            navigate({ to: '/facilities/$id', params: { id: row.id } })
          }
        />
      </div>
    </AdminLayout>
  );
}

// ─── New facility modal (Phase 14e-c-5) ────────────────────────────────────

// Rendered FROM the shared enum + labels, not a local copy. The previous
// hardcoded list carried an "if the schema adds more zones, add them here"
// note, and it duly went stale the moment America/Winnipeg was added — while
// also repeating the mislabelling of Atikokan as generic "NW Ontario". Reading
// the single source of truth removes the drift surface entirely; the portal's
// RecipientForm already does this.
const TIMEZONE_OPTIONS = (Object.entries(IANA_TIMEZONE_LABELS) as [IANATimezone, string][]).map(
  ([value, label]) => ({ value, label }),
);

const FACILITY_TYPE_OPTIONS = [
  { value: 'LTC_HOME', label: 'LTC home' },
  { value: 'RETIREMENT_RESIDENCE', label: 'Retirement residence' },
  { value: 'HOSPITAL', label: 'Hospital' },
  { value: 'OTHER', label: 'Other' },
] as const;

// Per-facility HST treatment (2026-09-21). Same enum as the rate card, but here it's
// the facility's own tax status — uniform across all roles, overriding the rate card
// for this facility's AR. Default TAXABLE; operator flags exempt facilities.
const HST_TREATMENT_OPTIONS = [
  { value: 'TAXABLE', label: 'Taxable (13% HST)' },
  { value: 'EXEMPT', label: 'Exempt (no HST)' },
  { value: 'ZERO_RATED', label: 'Zero-rated (0%)' },
] as const;

interface NewFacilityForm {
  name: string;
  dbaName: string;
  type: string;
  hstTreatment: string;
  addressLine1: string;
  city: string;
  postalCode: string;
  timezone: string;
  primaryContactName: string;
  primaryContactRole: string;
  primaryContactEmail: string;
  primaryContactPhone: string;
  billingContactEmail: string;
}

function NewFacilityModal({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form, setForm] = useState<NewFacilityForm>({
    name: '',
    dbaName: '',
    type: 'LTC_HOME',
    hstTreatment: 'TAXABLE',
    addressLine1: '',
    city: '',
    postalCode: '',
    timezone: 'America/Toronto',
    primaryContactName: '',
    primaryContactRole: '',
    primaryContactEmail: '',
    primaryContactPhone: '',
    billingContactEmail: '',
  });
  const [error, setError] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
       
      const r = (await unwrap(
        api.POST('/api/v1/admin/facilities' as never, { body } as never),
      )) as { id: string };
      return r;
    },
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['admin', 'facilities'] });
      navigate({ to: '/facilities/$id', params: { id: data.id } });
    },
  });

  // audit medium — trap focus within the dialog + close on Escape (replaces the
  // manual keydown listener; also keeps Tab from walking the background page).
  const panelRef = useRef<HTMLDivElement>(null);
  useFocusTrap(panelRef, { active: true, onEscape: onClose });

  function setField<K extends keyof NewFacilityForm>(key: K, value: NewFacilityForm[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function submit() {
    setError(null);
    // Client-side guard for the required-non-empty fields. Backend
    // enforces too via AdminFacilityCreateRequestSchema; this saves
    // a server round-trip on obviously-blank submissions.
    if (form.name.trim().length === 0) {
      setError('Facility name is required.');
      return;
    }
    if (
      form.primaryContactName.trim().length === 0 ||
      form.primaryContactEmail.trim().length === 0
    ) {
      setError(
        'A primary contact name and email are required — they become the facility’s first Admin.',
      );
      return;
    }
    try {
      await createMutation.mutateAsync({
        name: form.name.trim(),
        dbaName: form.dbaName.trim() || undefined,
        type: form.type,
        hstTreatment: form.hstTreatment,
        addressLine1: form.addressLine1.trim() || undefined,
        city: form.city.trim() || undefined,
        postalCode: form.postalCode.trim() || undefined,
        timezone: form.timezone,
        primaryContactName: form.primaryContactName.trim(),
        primaryContactRole: form.primaryContactRole.trim() || undefined,
        primaryContactEmail: form.primaryContactEmail.trim(),
        primaryContactPhone: form.primaryContactPhone.trim() || undefined,
        billingContactEmail: form.billingContactEmail.trim() || undefined,
      });
    } catch (e) {
      // Surface the BACKEND message (e.g. bug #11 "already associated with an
      // existing facility (X)"), not the generic "API 409 Conflict" that
      // e.message carries. messageOf reads ApiError.body.{message,reason}.
      setError(messageOf(e, 'Failed to create facility. Try again.'));
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="new-facility-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4"
      data-testid="new-facility-modal"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        className="w-full max-w-2xl rounded-md border border-border bg-cream p-6 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-start justify-between">
          <h2 id="new-facility-modal-title" className="font-heading text-xl text-forest">
            New facility
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
        <p className="mt-2 text-sm text-ink-muted">
          Creates the facility in PROSPECT status. Address geocoding fires
          automatically per Phase 14e-a-9.
        </p>

        {error && (
          <Alert tone="danger" className="mt-3" data-testid="new-facility-error">
            {error}
          </Alert>
        )}

        <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
          <Field label="Name (required)">
            <Input
              type="text"
              value={form.name}
              onChange={(e) => setField('name', e.target.value)}
              data-testid="new-facility-name"
            />
          </Field>
          <Field label="DBA name (optional)">
            <Input
              type="text"
              value={form.dbaName}
              onChange={(e) => setField('dbaName', e.target.value)}
            />
          </Field>
          <Field label="Type">
            <select
              value={form.type}
              onChange={(e) => setField('type', e.target.value)}
              className="w-full rounded-md border border-border bg-cream p-2 text-sm"
              data-testid="new-facility-type"
            >
              {FACILITY_TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="HST treatment">
            <select
              value={form.hstTreatment}
              onChange={(e) => setField('hstTreatment', e.target.value)}
              className="w-full rounded-md border border-border bg-cream p-2 text-sm"
              data-testid="new-facility-hst-treatment"
            >
              {HST_TREATMENT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Timezone">
            <select
              value={form.timezone}
              onChange={(e) => setField('timezone', e.target.value)}
              className="w-full rounded-md border border-border bg-cream p-2 text-sm"
              data-testid="new-facility-timezone"
            >
              {TIMEZONE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Address line 1">
            <Input
              type="text"
              value={form.addressLine1}
              onChange={(e) => setField('addressLine1', e.target.value)}
            />
          </Field>
          <Field label="City">
            <Input
              type="text"
              value={form.city}
              onChange={(e) => setField('city', e.target.value)}
            />
          </Field>
          <Field label="Postal code">
            <Input
              type="text"
              value={form.postalCode}
              onChange={(e) => setField('postalCode', e.target.value)}
            />
          </Field>
          <p className="col-span-2 mt-1 rounded-md bg-cream px-3 py-2 text-xs text-ink-muted">
            The primary contact becomes the facility’s first{' '}
            <strong className="text-ink">Admin</strong> login — they get a sign-in link and can then
            invite + manage their own team from the portal. Name + email are required.
          </p>
          <Field label="Primary contact name (required)">
            <Input
              type="text"
              value={form.primaryContactName}
              onChange={(e) => setField('primaryContactName', e.target.value)}
              data-testid="new-facility-contact-name"
            />
          </Field>
          <Field label="Primary contact title">
            <Input
              type="text"
              value={form.primaryContactRole}
              onChange={(e) => setField('primaryContactRole', e.target.value)}
            />
          </Field>
          <Field label="Primary contact email (required)">
            <Input
              type="email"
              value={form.primaryContactEmail}
              onChange={(e) => setField('primaryContactEmail', e.target.value)}
              data-testid="new-facility-contact-email"
            />
          </Field>
          <Field label="Primary contact phone">
            <Input
              type="tel"
              value={form.primaryContactPhone}
              onChange={(e) => setField('primaryContactPhone', e.target.value)}
            />
          </Field>
          <Field label="Billing contact email (optional)">
            <Input
              type="email"
              value={form.billingContactEmail}
              onChange={(e) => setField('billingContactEmail', e.target.value)}
            />
          </Field>
        </div>

        <div className="mt-5 flex items-center justify-end gap-3">
          <Button variant="secondary" onClick={onClose} disabled={createMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void submit()}
            loading={createMutation.isPending}
            data-testid="new-facility-submit"
          >
            Create facility
          </Button>
        </div>
      </div>
    </div>
  );
}
